import {
  ArchiveRestore,
  BarChart3,
  BookOpen,
  Building2,
  CalendarCheck,
  ClipboardCheck,
  Compass,
  FileCheck2,
  FileStack,
  FileUp,
  Flag,
  Gauge,
  Grid3x3,
  HardHat,
  Landmark,
  LayoutGrid,
  Network,
  NotebookPen,
  ShieldCheck,
  Target,
  TrendingUp,
  Users,
  Waypoints,
  Wrench,
  type LucideIcon,
} from 'lucide-react';

/**
 * The single navigation tree.
 *
 * Flat by design. The approved sidebar has no dropdowns and renders the same
 * list on every screen — only the active item changes. Second-level navigation
 * happens with page-level tabs inside a module, never in the sidebar.
 *
 * Everything derives from this: the collapsed icon rail, the expanded sidebar,
 * the mobile drawer, breadcrumbs and the router's placeholder routes. Adding a
 * screen means adding one entry here, so the surfaces cannot drift apart.
 */

export interface NavItem {
  id: string;
  label: string;
  icon: LucideIcon;
  path: string;
  /** Section heading this item sits under. */
  section: NavSection;
  /** Amber dot on the rail icon — unresolved items needing attention. */
  attention?: boolean;
  /** False while the screen is still a placeholder. Documents build state. */
  built?: boolean;
  /**
   * Who may see this item at all.
   *
   * `feedback` — the client and the delivery team only. The Development area is
   * a conversation about the software, not part of it, and a builder's staff
   * must never see that it exists.
   *
   * `executive` — system administrators, plus whoever can see the review layer.
   * The CEO dashboard carries pipeline value and cash position, which is not a
   * site supervisor's business. `non-executive` is its complement, for an entry
   * the executive audience reaches another way. Both are enforced on the server too; this only
   * decides what appears in the menu.
   */
  restricted?: 'feedback' | 'executive' | 'finance' | 'non-executive';
  /**
   * A crumb between Home and this item. The Company pages sit in the sidebar
   * but the client's designs title them "Home / Company / Checklists".
   */
  crumbParent?: { label: string; path: string };
  /**
   * The module this screen is a tab of. It leaves the sidebar — the module's
   * entry stands for it — and appears in the module's tab strip instead, so the
   * sidebar stays short while each screen keeps its own URL (client round 4).
   */
  module?: string;
  /** The label on the module's tab strip, when it differs from the sidebar's. */
  tabLabel?: string;
  /** Other paths that are the same screen — a page's own tabs — where the module strip also shows. */
  alsoOn?: string[];
  /**
   * Where the entry lives when it is not in the sidebar. `profile` — the
   * profile menu, beside Company settings (client round 6 moved Insights and
   * Finance there).
   */
  placement?: 'profile';
  /**
   * A tab that opens another module rather than being a screen of this one —
   * Site Management on the Projects strip. Never matched as the current page,
   * so the module it points at keeps its own sidebar entry and tabs.
   */
  linkOnly?: boolean;
}

export type NavSection =
  | 'overview'
  | 'business-development'
  | 'project-delivery'
  | 'directory'
  | 'safety-compliance'
  | 'resources'
  | 'finance'
  | 'company'
  | 'delivery';

/** `overview` is deliberately unlabelled — Dashboard sits above the first heading. */
export const NAV_SECTIONS: { id: NavSection; label: string }[] = [
  { id: 'overview', label: '' },
  { id: 'business-development', label: 'Business Development' },
  { id: 'project-delivery', label: 'Project Delivery' },
  { id: 'directory', label: 'Directory' },
  { id: 'safety-compliance', label: 'Safety & Compliance' },
  { id: 'resources', label: 'Resources' },
  { id: 'finance', label: 'Finance' },
  { id: 'company', label: 'Company' },
  { id: 'delivery', label: 'Delivery' },
];


/*
 * Some screens are deliberately absent from this list.
 *
 * **Company settings** — the company profile and its users — is a settings
 * screen, not a place you work. Client feedback round 2 (#28) asked for it off
 * the sidebar and behind the profile icon, where every other product puts its
 * settings — see the Topbar profile menu. The Company *section* below is
 * different: vision, org chart, matrix, scorecards, checklists and SOPs are the
 * operating model people work from, and the 13 Sep designs put them in the nav.
 *
 * **Marketing** is not part of OneParacon (decided 14 Sep 2026).
 *
 * **Internal Resources** became the SOP register (docs/13 item 11), which lives
 * under Company with the rest of the operating model.
 *
 * **Sign-On Register** had its own entry pointing at a tab of Site Management,
 * which read as two modules holding the same data (#34). It is one screen, so
 * it gets one entry: Site Management, whose Register tab is the register.
 *
 * **Documents** is gone entirely (#26). A document belongs to the work that
 * produced it — tender documents on the tender, permits and SWMS on the
 * project, employment records on the person — and a filing cabinet standing
 * outside that work is how documents get lost. There is no cross-module search
 * surface in its place; the client asked for it to go, not to move.
 */
export const NAV_ITEMS: NavItem[] = [
  // One dashboard per role (client round 6): an executive sees the CEO
  // Dashboard, everyone else this one. Both are customisable.
  { id: 'dashboard', label: 'Dashboard', icon: LayoutGrid, path: '/dashboard', section: 'overview', built: true, restricted: 'non-executive' },
  {
    id: 'ceo-dashboard',
    label: 'CEO Dashboard',
    icon: Gauge,
    path: '/dashboard/ceo',
    section: 'overview',
    built: true,
    restricted: 'executive',
  },

  /* Company — the operating model, one module with its parts as tabs (#47). */
  {
    id: 'company-values',
    label: 'Company',
    tabLabel: 'Vision, Mission & Values',
    icon: Compass,
    path: '/company/about',
    section: 'company',
    built: true,
  },
  { id: 'company-org-chart', label: 'Organisational Chart', icon: Network, path: '/company/org-chart', section: 'company', module: 'company-values', built: true },
  { id: 'company-roles', label: 'Roles & Responsibilities', icon: Grid3x3, path: '/company/roles', section: 'company', module: 'company-values', built: true },
  { id: 'company-kpi-scorecards', label: 'KPI Scorecards', icon: TrendingUp, path: '/company/kpi-scorecards', section: 'company', module: 'company-values', built: true },
  { id: 'company-sops', label: 'SOP Register', icon: BookOpen, path: '/company/sops', section: 'company', module: 'company-values', built: true },
  { id: 'company-lessons', label: 'Lessons Learned', icon: BookOpen, path: '/company/lessons', section: 'company', module: 'company-values', built: true },
  { id: 'company-archive', label: 'Archive', icon: ArchiveRestore, path: '/company/archive', section: 'company', module: 'company-values', built: true },
  // The forms teams work from and the documents the company issues. Editable
  // from the screen, because the client will keep changing them (round 6).
  { id: 'company-templates', label: 'Templates', icon: FileStack, path: '/company/templates', section: 'company', module: 'company-values', built: true },
  {
    id: 'company-data-import',
    label: 'Data Import',
    icon: FileUp,
    path: '/company/data-import',
    section: 'company',
    module: 'company-values',
    built: true,
    // Administrators and the client — the executive audience. The API enforces it.
    restricted: 'executive',
  },

  /* Business Development — one module, the pre-construction flow from first contact to bid. */
  { id: 'pre-construction', label: 'Pre-Construction', tabLabel: 'Dashboard', icon: ClipboardCheck, path: '/pre-construction', section: 'business-development' },
  { id: 'prospects', label: 'Prospects', icon: Target, path: '/prospects', section: 'business-development', module: 'pre-construction', alsoOn: ['/prospects/follow-ups', '/prospects/interactions'] },
  // The checklist lives inside each tender now — one place, where the work is (round 6).
  { id: 'tenders', label: 'Tenders', tabLabel: 'Register', icon: ClipboardCheck, path: '/pre-construction/tenders', section: 'business-development', module: 'pre-construction' },

  /* Project Delivery — the job, its programme and its money (#45). */
  /*
   * Round 6: Dashboard, Projects, Lessons Learned and Site Management. The
   * programme, delivery checklist and finance belong to one job, so they are
   * tabs inside each project rather than module-wide screens.
   */
  { id: 'projects', label: 'Projects', tabLabel: 'Dashboard', icon: Waypoints, path: '/projects', section: 'project-delivery' },
  { id: 'project-register', label: 'Projects', icon: Waypoints, path: '/projects/register', section: 'project-delivery', module: 'projects', built: true },
  { id: 'projects-site-management', label: 'Site Management', icon: HardHat, path: '/site-management', section: 'project-delivery', module: 'projects', built: true, linkOnly: true },
  {
    // The company-wide ledgers; a job's own money is its Finance tab.
    id: 'finance',
    label: 'Finance',
    icon: Landmark,
    path: '/finance',
    section: 'project-delivery',
    placement: 'profile',
    alsoOn: ['/finance/budgets', '/finance/purchase-orders', '/finance/expenses', '/finance/claims', '/finance/inventory', '/finance/cost-codes'],
    built: true,
    // Budgets, bills and margins. A Project Admin reaches them without the
    // company-wide view, so this is its own gate. The API enforces it.
    restricted: 'finance',
  },

  /* Site Management — everything the site team reaches for daily, in one place (#43, #44). */
  {
    id: 'site-management',
    label: 'Site Management',
    tabLabel: 'Attendance',
    icon: HardHat,
    path: '/site-management',
    section: 'project-delivery',
    alsoOn: ['/site-management/register', '/site-management/warnings', '/site-management/timesheets', '/site-management/quick-hours'],
  },
  { id: 'weekly-attendance', label: 'Weekly Attendance', icon: CalendarCheck, path: '/site-management/weekly', section: 'project-delivery', module: 'site-management', built: true },
  { id: 'site-diaries', label: 'Site Diaries', icon: NotebookPen, path: '/site-diaries', section: 'project-delivery', module: 'site-management', built: true },
  { id: 'defects', label: 'Defects', icon: Flag, path: '/defects', section: 'project-delivery', module: 'site-management', built: true },
  {
    id: 'safety-compliance',
    label: 'Safety & Compliance',
    icon: ShieldCheck,
    path: '/safety',
    section: 'project-delivery',
    module: 'site-management',
    alsoOn: ['/safety/certifications', '/safety/documents', '/safety/inductions', '/safety/requirements', '/safety/types'],
    attention: true,
    built: true,
  },
  {
    // Company documents: insurances, pre-qualifications, certifications, SWMS and SDS.
    id: 'company-compliance',
    label: 'Company Compliance',
    icon: FileCheck2,
    path: '/compliance',
    section: 'project-delivery',
    module: 'site-management',
    built: true,
  },

  {
    // The module's numbers and widgets, first on the strip — they used to sit
    // above every People screen (client round 7).
    id: 'people-dashboard',
    label: 'Dashboard',
    icon: LayoutGrid,
    path: '/people/dashboard',
    section: 'directory',
    module: 'people',
    built: true,
  },
  {
    // Clients, suppliers and subcontractors — the companies the people belong to (client round 6).
    id: 'business-directory',
    label: 'Companies',
    icon: Building2,
    path: '/business-directory',
    section: 'directory',
    module: 'people',
    built: true,
  },
  {
    id: 'people',
    label: 'People',
    icon: Users,
    path: '/people',
    section: 'directory',
    alsoOn: ['/people/onboarding', '/people/skills-matrix', '/people/scorecards', '/people/staff', '/people/workers'],
    built: true,
  },

  { id: 'warehouse', label: 'Warehouse', icon: Wrench, path: '/warehouse', section: 'resources' },
  {
    id: 'insights',
    label: 'Insights',
    icon: BarChart3,
    path: '/insights',
    section: 'resources',
    built: true,
    placement: 'profile',
  },

];

/**
 * The nav a given user actually sees.
 *
 * Every surface — rail, sidebar, mobile drawer — goes through this, so a
 * restricted item cannot leak into one of them by being added to only two.
 */
export function visibleNavItems(access: {
  feedback: boolean;
  executive: boolean;
  /** May open Finance. Falls back to the executive answer when not given. */
  finance?: boolean;
}): NavItem[] {
  return NAV_ITEMS.filter((item) => {
    if (item.restricted === 'feedback') return access.feedback;
    if (item.restricted === 'executive') return access.executive;
    if (item.restricted === 'finance') return access.finance ?? access.executive;
    if (item.restricted === 'non-executive') return !access.executive;

    return true;
  });
}

/** The sidebar's entries: top-level modules, not their tabs or profile-menu items. */
export function sidebarNavItems(access: { feedback: boolean; executive: boolean; finance?: boolean }): NavItem[] {
  const visible = visibleNavItems(access);

  return visible
    .filter((item) => !item.module && !item.placement)
    // A module's entry carries its tabs' attention dot.
    .map((item) => ({ ...item, attention: item.attention || visible.some((child) => child.module === item.id && child.attention) }));
}

/** The home screen for a role: the CEO Dashboard for executives, the Dashboard for everyone else. */
export function homePathFor(access: { executive: boolean }): string {
  return access.executive ? '/dashboard/ceo' : '/dashboard';
}

/**
 * Labels for routes that live inside a module rather than in the sidebar.
 *
 * The sidebar is flat, but breadcrumbs still need to name where you are — the
 * approved screens show `Home / People / Staff / Michael Carter`. Deeper record
 * segments (a person's name) are appended by the page itself.
 */
export const SUB_PAGE_LABELS: Record<string, string> = {
  '/compliance/documents': 'Document',
  '/notifications': 'Notifications',
  '/dashboard/ceo': 'CEO Dashboard',
  // Reached from the profile menu rather than the sidebar — see the note above.
  '/company': 'Company settings',
  '/company/users': 'Admin users',
  '/company/permissions': 'Roles & permissions',
  '/company/lessons': 'Lessons Learned',
  '/company/archive': 'Archive',
  '/projects/programme': 'Programme',
  '/projects/checklist': 'Project Delivery Checklist',
  '/pre-construction/checklist': 'Tender Checklist',
  '/insights': 'Insights',
  '/site-management/register': 'Sign-On Register',
  '/site-management/timesheets': 'Timesheets',
  '/site-management/quick-hours': 'Quick hours',
  '/people/dashboard': 'Dashboard',
  '/people/staff': 'Staff',
  '/people/workers': 'Workers',
  '/pre-construction/tenders': 'Register',
  '/finance/budgets': 'Budgets',
  '/finance/purchase-orders': 'Purchase Orders',
  '/finance/expenses': 'Bills & Expenses',
  '/finance/claims': 'Progress Claims',
  '/finance/inventory': 'Inventory',
  '/finance/cost-codes': 'Cost Codes',
  '/finance/projects': 'Project Finance',
};

/** The sidebar entry for a screen: the module it belongs to, or itself. */
/**
 * Screens that read the sidebar's project, and therefore show the picker.
 *
 * The picker is not navigation. It is the scoping control for the modules that
 * live outside a project's own page: a site diary, a defect, a day's hours and
 * a finance tab all belong to one job, and this is how they are told which.
 * Two of them refuse to work without it — Site Diaries says so in words, and
 * Quick Hours disables its save.
 *
 * Everywhere else it was furniture, and worse than furniture: a control that
 * does nothing on the screen you are looking at teaches people it does nothing
 * anywhere. The company's own pages, the tender register, the warehouse and a
 * project's own page — which carries its project in the URL — hide it.
 */
const PROJECT_SCOPED_PREFIXES = [
  '/dashboard',
  '/site-management',
  '/site-diaries',
  '/defects',
  '/compliance',
  '/safety',
  '/finance',
  '/insights',
  '/agent',
  '/projects/programme',
];

/**
 * The people screens filter by job; one person's record does not.
 *
 * Exact, because `/people` would otherwise match `/people/<id>`, and a picker
 * on somebody's record is the furniture this is removing.
 */
const PROJECT_SCOPED_EXACT = ['/people', '/people/dashboard'];

export function needsProjectSelector(pathname: string): boolean {
  return (
    PROJECT_SCOPED_EXACT.includes(pathname) ||
    PROJECT_SCOPED_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))
  );
}

export function sidebarItemFor(pathname: string): NavItem | undefined {
  const { item } = findNavMatch(pathname);
  return item?.module ? NAV_ITEMS.find((entry) => entry.id === item.module) : item;
}

/**
 * The tab strip for the module a screen belongs to — only on the module's own
 * screens (a tab's path, or one of its page tabs), not on a record opened from
 * one, which has its own back link and tabs.
 */
export function moduleTabsFor(pathname: string, access: { feedback: boolean; executive: boolean; finance?: boolean }): { items: NavItem[]; active: NavItem } | null {
  const { item } = findNavMatch(pathname);
  if (!item) return null;

  const moduleId = item.module ?? item.id;
  const items = visibleNavItems(access).filter((entry) => entry.id === moduleId || entry.module === moduleId);
  if (items.length < 2) return null;

  const onScreen = items.some((entry) => entry.path === pathname || entry.alsoOn?.includes(pathname));
  return onScreen ? { items, active: item } : null;
}

/** Locate the nav item matching a pathname, longest match wins. */
export function findNavMatch(pathname: string): { item?: NavItem } {
  let best: { item?: NavItem; length: number } = { length: -1 };

  for (const item of NAV_ITEMS) {
    if (item.linkOnly) continue;
    if (
      (pathname === item.path || pathname.startsWith(`${item.path}/`)) &&
      item.path.length > best.length
    ) {
      best = { item, length: item.path.length };
    }
  }

  return { item: best.item };
}

/** The sub-page a path sits on, if any. Longest match wins — /people/staff beats /people. */
export function subPageFor(pathname: string): { path: string; label: string } | undefined {
  const match = Object.keys(SUB_PAGE_LABELS)
    .filter((path) => pathname === path || pathname.startsWith(`${path}/`))
    .sort((a, b) => b.length - a.length)[0];

  return match ? { path: match, label: SUB_PAGE_LABELS[match]! } : undefined;
}

/** The most specific label for a path — used for page titles. */
export function labelForPath(pathname: string): string {
  return subPageFor(pathname)?.label ?? findNavMatch(pathname).item?.label ?? 'Coming soon';
}

/** Breadcrumb trail for the top bar: Home / People / Staff. */
export function breadcrumbsFor(pathname: string): { label: string; path?: string }[] {
  const { item } = findNavMatch(pathname);
  const trail: { label: string; path?: string }[] = [{ label: 'Home', path: '/dashboard' }];

  if (item?.crumbParent) trail.push(item.crumbParent);
  const parent = item?.module ? NAV_ITEMS.find((entry) => entry.id === item.module) : undefined;
  if (parent) trail.push({ label: parent.label, path: parent.path });
  if (item && item.id !== 'dashboard') trail.push({ label: item.label, path: item.path });

  // A Company page is its own nav item, so the settings sub-page label for
  // `/company` must not be appended after it as well.
  const sub = subPageFor(pathname);
  if (sub && sub.path !== item?.path && !(item?.crumbParent && sub.path === item.crumbParent.path)) {
    trail.push({ label: sub.label, path: sub.path });
  }

  return trail;
}

/**
 * Where a Back control on `pathname` should land.
 *
 * The client asked for a back link on every page ("typical comment
 * throughout"). Browser history alone is not enough — a tender link pasted into
 * a chat opens with nothing behind it — so the answer is derived from the nav
 * tree instead: the nearest ancestor the app can actually render. A top-level
 * screen returns `undefined` and shows no control, because there is nowhere
 * above it to go.
 */
export function parentPathFor(pathname: string): string | undefined {
  const path = pathname.replace(/\/+$/, '') || '/';

  const sub = subPageFor(path);
  if (sub && sub.path !== path) return sub.path;

  const { item } = findNavMatch(path);
  if (item && item.path !== path) return item.path;

  // A route outside the nav tree still has an ancestor worth offering.
  const cut = path.lastIndexOf('/');
  return cut > 0 ? path.slice(0, cut) : undefined;
}

